// CDR, recordings, reports.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Download, Play } from 'lucide-react';
import { api, download, blobUrl, errorText } from '@/lib/api';
import { PageHeader, DataTable, Pager, ErrorBox, Field, Stat, Loading } from '@/components/common';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useRefOptions } from '@/hooks/meta';
import { fmtTime, fmtDuration, pct, toUtcParam, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { useCan } from '@/store/auth';
import { toast } from '@/store/toast';

function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function startOfToday(): string { const d = new Date(); d.setHours(0, 0, 0, 0); return localInput(d); }

function useRange() {
  const [from, setFrom] = React.useState(startOfToday());
  const [to, setTo] = React.useState('');
  return { from, to, setFrom, setTo, query: { from: toUtcParam(from), to: to ? toUtcParam(to) : undefined } };
}

function RangeInputs({ r }: { r: ReturnType<typeof useRange> }) {
  return (<>
    <Field help="records.from" label={t('From')}><Input type="datetime-local" value={r.from} onChange={(e) => r.setFrom(e.target.value)} /></Field>
    <Field help="records.to" label={t('To')}><Input type="datetime-local" value={r.to} onChange={(e) => r.setTo(e.target.value)} /></Field>
  </>);
}

// ---- CDR -------------------------------------------------------------------------------------------
export function CdrPage() {
  const r = useRange();
  const campaigns = useRefOptions('campaigns');
  const [direction, setDirection] = React.useState('');
  const [campaign, setCampaign] = React.useState('');
  const [number, setNumber] = React.useState('');
  const [answered, setAnswered] = React.useState('');
  const [minWait, setMinWait] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [detail, setDetail] = React.useState<string | null>(null);
  const canExport = useCan('cdr.export');
  const filters = { ...r.query, direction, campaign_id: campaign, number, answered, min_wait: minWait };
  const rows = useQuery({ queryKey: ['cdr', filters, offset], queryFn: () => api<{ rows: any[]; total: number }>('/cdr', { query: { ...filters, limit: 25, offset } }) });
  return (
    <div>
      <PageHeader title="CDR" description={t('One record per call, written by the engine from VoIP switch events.')}>
        {canExport && <Button variant="outline" onClick={() => download('/cdr/export.csv', { ...filters, limit: 100000 }, 'cdr.csv').catch((e) => toast.error(errorText(e)))}><Download /> {t('Export CSV')}</Button>}
      </PageHeader>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <RangeInputs r={r} />
        <Field help="cdr.direction" label={t('Direction')}><Select value={direction} onChange={(e) => setDirection(e.target.value)}><option value="">{t('All')}</option>{['OUTBOUND', 'INBOUND', 'INTERNAL'].map((v) => <option key={v} value={v}>{enumLabel(v)}</option>)}</Select></Field>
        <Field help="cdr.campaign" label={t('Campaign')}><Select value={campaign} onChange={(e) => setCampaign(e.target.value)}><option value="">{t('All')}</option>{(campaigns.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        <Field help="cdr.answered" label={t('Answered')}><Select value={answered} onChange={(e) => setAnswered(e.target.value)}><option value="">{t('All')}</option><option value="1">{t('Answered')}</option><option value="0">{t('Not answered')}</option></Select></Field>
        <Field help="cdr.min_wait" label={t('Waited at least (s)')}><Input className="w-28" type="number" min={0} value={minWait} onChange={(e) => setMinWait(e.target.value)} data-testid="cdr-min-wait" /></Field>
        <Field help="cdr.number" label={t('Number')}><Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={t('part of a number')} /></Field>
      </div>
      <ErrorBox error={rows.error} />
      <DataTable rowKey={(x) => x.call_uuid} rows={rows.data?.rows} onRowClick={(x) => setDetail(x.call_uuid)} columns={[
        { key: 'start_time', label: t('Start'), render: (x) => fmtTime(x.start_time) }, { key: 'direction', label: t('Dir'), render: (x) => enumLabel(x.direction) },
        { key: 'source', label: t('From') }, { key: 'destination', label: t('To') }, { key: 'agent_code', label: t('Agent') },
        { key: 'campaign_id', label: t('Campaign') }, { key: 'duration', label: t('Duration'), render: (x) => fmtDuration(x.duration) },
        { key: 'billsec', label: t('Billed'), render: (x) => fmtDuration(x.billsec) }, { key: 'talk_sec', label: t('Talk'), render: (x) => x.talk_sec == null ? '-' : fmtDuration(x.talk_sec) },
        { key: 'wait_sec', label: t('Wait'), render: (x) => x.wait_sec == null ? '-' : fmtDuration(x.wait_sec) },
        { key: 'hangup_cause', label: t('Cause') }, { key: 'disposition', label: t('Dispo') }]} />
      <Pager offset={offset} limit={25} total={rows.data?.total} onChange={setOffset} />
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        {detail && <DialogContent title={t('Call detail')} className="max-w-4xl"><CallDetail uuid={detail} /></DialogContent>}
      </Dialog>
    </div>
  );
}

const OFFER_COLS = ['agent_id', 'result', 'cause', 'offered_at', 'ended_at'];

function CallDetail({ uuid }: { uuid: string }) {
  const d = useQuery({ queryKey: ['cdr-detail', uuid], queryFn: () => api(`/cdr/${uuid}`) });
  if (d.isLoading) return <Loading />;
  if (d.error) return <ErrorBox error={d.error} />;
  const x = d.data;
  const section = (title: string, rows: any[] | undefined, cols: string[]) => rows && rows.length > 0 && (
    <div><h4 className="mb-1 text-sm font-semibold">{title}</h4>
      <DataTable rows={rows} columns={cols.map((c) => ({ key: c, label: t(c.replace(/_/g, ' ')), render: /(_at|_time)$/.test(c) ? (r: any) => fmtTime(r[c]) : undefined }))} /></div>
  );
  return (
    <div className="grid gap-4 text-sm">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
        {x.cdr && ['call_uuid', 'direction', 'source', 'destination', 'agent_code', 'campaign_id', 'queue_id', 'trunk_id', 'hangup_cause', 'sip_response', 'disposition', 'duration', 'billsec', 'talk_sec', 'wait_sec']
          .concat(x.cdr.broadcast_id ? ['broadcast_id', 'contact_id'] : [])
          .map((k) => <div key={k}><span className="text-muted-foreground">{t(k.replace(/_/g, ' '))}:</span> {String(x.cdr[k] ?? '-')}</div>)}
      </div>
      {x.cdr?.survey_answers && typeof x.cdr.survey_answers === 'object' && (
        <div data-testid="cdr-survey"><h4 className="mb-1 text-sm font-semibold">{t('Survey answers')}</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-1">{Object.entries(x.cdr.survey_answers).map(([k, v]) => (
            <div key={k}><span className="font-mono text-xs text-muted-foreground">{k}:</span> {String(v)}</div>))}</div></div>
      )}
      {x.recordings?.length > 0 && <div className="flex flex-wrap gap-2">{x.recordings.map((r: any) => <RecordingPlayer key={r.id} id={r.id} />)}</div>}
      {x.routes && <div><h4 className="mb-1 text-sm font-semibold">{t('Routing')}</h4>
        <div>{t('Trunk plan {{plan}}, attempts {{attempts}}, causes {{causes}}', { plan: String(x.routes.plan ?? '-'), attempts: String(x.routes.attempt ?? 0), causes: String(x.routes.causes ?? '-') })}</div></div>}
      {section(t('Queue offers'), x.offers, OFFER_COLS)}
      {section(t('Agent states'), x.agent_states, ['agent_id', 'from_state', 'to_state', 'reason', 'created_at'])}
      {section(t('Dispositions'), x.dispositions, ['agent_id', 'code', 'notes', 'created_at'])}
      {section('IVR', x.ivr, ['menu_id', 'event', 'digits', 'action', 'result', 'created_at'])}
    </div>
  );
}

export function RecordingPlayer({ id }: { id: number }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const allowed = useCan('recordings.play');
  React.useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio src={url} controls autoPlay className="h-8" />;
  if (!allowed) return null;
  return <Button size="sm" variant="outline" onClick={() => blobUrl(`/recordings/${id}/file`).then(setUrl).catch((e) => toast.error(errorText(e)))}><Play /> {t('Play')}</Button>;
}

// ---- Recordings -----------------------------------------------------------------------------------
export function RecordingsPage() {
  const r = useRange();
  const agents = useRefOptions('agents');
  const [agent, setAgent] = React.useState('');
  const query = { ...r.query, agent_id: agent, limit: 200 };
  const rows = useQuery({ queryKey: ['recordings', query], queryFn: () => api<{ rows: any[] }>('/recordings', { query }) });
  return (
    <div>
      <PageHeader title={t('Recordings')} description={t('Call recordings made by the VoIP switch. Playback streams through the API with your session.')} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <RangeInputs r={r} />
        <Field help="recordings.agent" label={t('Agent')}><Select value={agent} onChange={(e) => setAgent(e.target.value)}><option value="">{t('All')}</option>{(agents.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.agent_code} {a.name}</option>)}</Select></Field>
      </div>
      <ErrorBox error={rows.error} />
      <DataTable rows={rows.data?.rows} columns={[
        { key: 'started_at', label: t('Start'), render: (x) => fmtTime(x.started_at) }, { key: 'source', label: t('From') }, { key: 'destination', label: t('To') },
        { key: 'agent_id', label: t('Agent') }, { key: 'campaign_id', label: t('Campaign') }, { key: 'billsec', label: t('Billed'), render: (x) => fmtDuration(x.billsec) },
        { key: 'format', label: t('Format') }]}
        actions={(x) => <RecordingPlayer id={x.id} />} />
    </div>
  );
}

// ---- Reports ----------------------------------------------------------------------------------------
export function ReportsPage() {
  const [from, setFrom] = React.useState('today');
  const [custom, setCustom] = React.useState(startOfToday());
  const f = from === 'custom' ? toUtcParam(custom) : from;
  const summary = useQuery({ queryKey: ['rep-summary', f], queryFn: () => api('/reports/summary', { query: { from: f } }) });
  const s = summary.data ?? {};
  const o = s.outbound ?? {};
  const i = s.inbound ?? {};
  const [params] = useSearchParams();
  return (
    <div className="grid gap-4">
      <PageHeader title={t('Reports')} description={t('Computed by the engine from the CDR and agent state history.')}>
        <Select className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('Period')}>
          <option value="-15m">{t('Last 15 minutes')}</option><option value="-1h">{t('Last hour')}</option><option value="today">{t('Today')}</option>
          <option value="-1d">{t('Last 24 hours')}</option><option value="-7d">{t('Last 7 days')}</option><option value="custom">{t('Since…')}</option>
        </Select>
        {from === 'custom' && <Input type="datetime-local" className="w-56" value={custom} onChange={(e) => setCustom(e.target.value)} />}
      </PageHeader>
      <ErrorBox error={summary.error} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Stat label={t('Calls')} value={s.calls ?? '-'} />
        <Stat label="ASR" value={pct(o.asr)} hint={t('{{a}} of {{b}}', { a: o.answered ?? 0, b: o.attempts ?? 0 })} />
        <Stat label="ACD" value={fmtDuration(o.acd_sec)} />
        <Stat label={t('Busy')} value={pct(o.busy_rate)} />
        <Stat label={t('No answer')} value={pct(o.no_answer_rate)} />
        <Stat label={t('Abandon out')} value={pct(o.abandon_rate)} />
        <Stat label={t('Abandon in')} value={pct(i.abandon_rate)} hint={t('wait {{d}}', { d: fmtDuration(i.avg_wait_sec) })} />
        <Stat label={t('Peak')} value={`${s.concurrent_peak ?? 0}`} hint={t('concurrent · {{n}} CPS', { n: s.cps_peak ?? 0 })} />
      </div>
      <Tabs defaultValue={params.get('tab') ?? 'campaign'}>
        <TabsList>
          <TabsTrigger value="campaign">{t('By campaign')}</TabsTrigger><TabsTrigger value="agents">{t('Agents')}</TabsTrigger>
          <TabsTrigger value="trunk">{t('By trunk')}</TabsTrigger><TabsTrigger value="queue">{t('By queue')}</TabsTrigger><TabsTrigger value="waits">{t('Wait time')}</TabsTrigger><TabsTrigger value="sla">SLA</TabsTrigger>
          <TabsTrigger value="hour">{t('By hour')}</TabsTrigger><TabsTrigger value="day">{t('By day')}</TabsTrigger><TabsTrigger value="minutes">{t('Per minute')}</TabsTrigger>
        </TabsList>
        {['campaign', 'trunk', 'queue', 'hour', 'day'].map((g) => <TabsContent key={g} value={g} className="mt-3"><ByGroup group={g} from={f} /></TabsContent>)}
        <TabsContent value="agents" className="mt-3"><AgentReport from={f} /></TabsContent>
        <TabsContent value="waits" className="mt-3"><WaitReport from={f} /></TabsContent>
        <TabsContent value="sla" className="mt-3"><SlaReport from={f} /></TabsContent>
        <TabsContent value="minutes" className="mt-3"><Minutes /></TabsContent>
      </Tabs>
    </div>
  );
}

function ByGroup({ group, from }: { group: string; from: string }) {
  const q = useQuery({ queryKey: ['rep-by', group, from], queryFn: () => api('/reports/by/' + group, { query: { from } }) });
  const rows: any[] = Array.isArray(q.data?.rows) ? q.data.rows : [];
  return (<>
    <ErrorBox error={q.error} />
    <DataTable rowKey={(r) => String(r.key ?? 'none')} rows={rows} columns={[
      { key: 'key', label: t(group), render: (r) => r.key ?? t('(none)') }, { key: 'calls', label: t('Calls') }, { key: 'attempts', label: t('Attempts') },
      { key: 'answered', label: t('Answered') }, { key: 'asr', label: 'ASR', render: (r) => pct(r.asr) }, { key: 'acd_sec', label: 'ACD', render: (r) => fmtDuration(r.acd_sec) },
      { key: 'busy', label: t('Busy') }, { key: 'no_answer', label: t('No answer') }, { key: 'abandon_rate', label: t('Abandon'), render: (r) => pct(r.abandon_rate) },
      { key: 'talk_sec', label: t('Talk'), render: (r) => fmtDuration(r.talk_sec) }]} />
  </>);
}

// ---- queue wait time -----------------------------------------------------------------------------
const secs = (v: unknown) => (v == null ? '-' : fmtDuration(v));
const WAIT_COLS = ['offered', 'answered', 'abandoned', 'avg_wait_sec', 'p90_wait_sec', 'max_wait_sec', 'avg_abandon_wait_sec'];

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function waitCsv(d: any): string {
  const lines = [['section', 'key', ...WAIT_COLS].join(',')];
  const add = (section: string, key: unknown, r: any) => lines.push([section, key, ...WAIT_COLS.map((c) => r?.[c])].map(csvCell).join(','));
  add('total', '', d.total);
  for (const q of d.queues ?? []) add('queue', q.name ?? q.queue_id, q);
  for (const a of d.agents ?? []) add('agent', a.code ?? a.agent_id, a);
  for (const h of d.hours ?? []) add('hour_utc', h.key, h);
  for (const b of d.buckets ?? []) lines.push(['bucket', b.to_sec == null ? `${b.from_sec}+` : `${b.from_sec}-${b.to_sec}`, b.calls].map(csvCell).join(','));
  return lines.join('\n') + '\n';
}

function WaitTable({ rows, first, testid }: { rows: any[]; first: { key: string; label: string; render?: (r: any) => React.ReactNode }; testid: string }) {
  return (
    <div data-testid={testid}>
      <DataTable rowKey={(r) => String(r.key ?? 'none')} rows={rows} columns={[first,
        { key: 'offered', label: t('Calls') }, { key: 'answered', label: t('Answered') }, { key: 'abandoned', label: t('Abandoned') },
        { key: 'avg_wait_sec', label: t('Avg wait'), render: (r) => secs(r.avg_wait_sec) },
        { key: 'p90_wait_sec', label: t('90% within'), render: (r) => secs(r.p90_wait_sec) },
        { key: 'max_wait_sec', label: t('Longest'), render: (r) => secs(r.max_wait_sec) },
        { key: 'avg_abandon_wait_sec', label: t('Avg wait before abandon'), render: (r) => secs(r.avg_abandon_wait_sec) }]} />
    </div>
  );
}

function WaitReport({ from }: { from: string }) {
  const queues = useRefOptions('queues');
  const [queue, setQueue] = React.useState('');
  const q = useQuery({ queryKey: ['rep-waits', from, queue], queryFn: () => api('/reports/waits', { query: { from, queue: queue || undefined } }) });
  const d = q.data ?? {};
  const tot = d.total ?? {};
  const buckets: any[] = Array.isArray(d.buckets) ? d.buckets : [];
  const max = Math.max(1, ...buckets.map((b) => Number(b.calls) || 0));
  function exportCsv() {
    const url = URL.createObjectURL(new Blob([waitCsv(d)], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'wait-time.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <Field help="reports.wait_queue" label={t('Queue')}><Select className="w-48" value={queue} onChange={(e) => setQueue(e.target.value)} data-testid="waits-queue">
          <option value="">{t('All queues')}</option>{(queues.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></Field>
        <Button variant="outline" onClick={exportCsv} disabled={!q.data}><Download /> {t('Export CSV')}</Button>
      </div>
      <ErrorBox error={q.error} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label={t('Queue calls')} value={tot.offered ?? 0} hint={t('{{answered}} answered · {{abandoned}} abandoned', { answered: tot.answered ?? 0, abandoned: tot.abandoned ?? 0 })} />
        <Stat label={t('Avg wait')} value={secs(tot.avg_wait_sec)} hint={t('answered calls')} />
        <Stat label={t('90% answered within')} value={secs(tot.p90_wait_sec)} />
        <Stat label={t('Longest wait')} value={secs(tot.max_wait_sec)} hint={t('answered calls')} />
        <Stat label={t('Avg wait before abandon')} value={secs(tot.avg_abandon_wait_sec)} />
      </div>
      <Card>
        <CardHeader><CardTitle>{t('How long answered callers waited')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-1.5" data-testid="waits-buckets">
            {buckets.map((b) => {
              const label = b.to_sec == null ? t('over {{d}}', { d: fmtDuration(b.from_sec) }) : t('{{from}} to {{to}}', { from: fmtDuration(b.from_sec), to: fmtDuration(b.to_sec) });
              return (
                <div key={b.from_sec} className="flex items-center gap-2 text-sm">
                  <div className="w-32 shrink-0 text-muted-foreground">{label}</div>
                  <div className="h-4 flex-1 rounded bg-muted"><div className="h-4 rounded bg-primary/70" style={{ width: `${(Number(b.calls) / max) * 100}%` }} /></div>
                  <div className="w-12 text-right tabular-nums" data-testid={`waits-bucket-${b.from_sec}`}>{b.calls}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle>{t('Per queue')}</CardTitle></CardHeader><CardContent>
        <WaitTable testid="waits-queues" rows={d.queues ?? []} first={{ key: 'name', label: t('Queue'), render: (r) => r.name ?? r.queue_id }} />
      </CardContent></Card>
      <Card><CardHeader><CardTitle>{t('Per agent (calls they answered)')}</CardTitle></CardHeader><CardContent>
        <WaitTable testid="waits-agents" rows={d.agents ?? []} first={{ key: 'code', label: t('Agent'), render: (r) => r.code ?? r.agent_id }} />
      </CardContent></Card>
      <Card><CardHeader><CardTitle>{t('Per hour')}</CardTitle></CardHeader><CardContent>
        <WaitTable testid="waits-hours" rows={d.hours ?? []} first={{ key: 'key', label: t('Hour'), render: (r) => fmtTime(String(r.key) + ':00') }} />
      </CardContent></Card>
    </div>
  );
}

// ---- SLA (PHASE25) ---------------------------------------------------------------------------------
const pc = (v: unknown) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);
const SLA_COLS = ['offered', 'counted', 'answered', 'answered_in_sla', 'service_level_pct', 'abandoned', 'abandon_pct', 'short_abandoned', 'avg_answer_sec'];

function slaCsv(d: any): string {
  const lines = [['section', 'key', ...SLA_COLS, 'answer_sec', 'target_pct', 'abandon_max_pct', 'breach'].join(',')];
  const add = (section: string, key: unknown, r: any) => lines.push([section, key, ...SLA_COLS.map((c) => r?.[c]), r?.answer_sec, r?.target_pct, r?.abandon_max_pct,
    r?.breach ? [r.breach.service_level && 'service_level', r.breach.abandon && 'abandon'].filter(Boolean).join(' ') : ''].map(csvCell).join(','));
  if (d.total) add('total', '', d.total);
  for (const q of d.queues ?? []) add('queue', q.name, q);
  for (const p of d.periods ?? []) add(`${d.by}_utc`, p.key, p);
  for (const a of d.alerts ?? []) lines.push(['alert', a.queue_name, a.kind, a.opened_at, a.closed_at ?? '', a.opened_value, a.worst_value, a.target_value, a.waiting_at_open, a.acked_by ?? ''].map(csvCell).join(','));
  return lines.join('\n') + '\n';
}

function SlaCell({ value, target, bad, kind }: { value: unknown; target?: number; bad?: boolean; kind: 'sl' | 'ab' }) {
  return (
    <span className={bad ? 'font-semibold text-destructive' : value == null ? '' : 'text-success'}>
      {pc(value)}{target != null && <span className="ml-1 text-xs font-normal text-muted-foreground">{kind === 'sl' ? '≥' : '≤'} {target}%</span>}
    </span>
  );
}

function SlaReport({ from }: { from: string }) {
  const queues = useRefOptions('queues');
  const [queue, setQueue] = React.useState('');
  const q = useQuery({ queryKey: ['rep-sla', from, queue], queryFn: () => api('/reports/sla', { query: { from, queue: queue || undefined } }) });
  const d = q.data ?? {};
  const tot = d.total ?? {};
  const s = d.settings ?? {};
  const periods: any[] = Array.isArray(d.periods) ? d.periods : [];
  function exportCsv() {
    const url = URL.createObjectURL(new Blob([slaCsv(d)], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'sla.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <div className="grid gap-4" data-testid="sla-report">
      <div className="flex flex-wrap items-end gap-2">
        <Field help="reports.sla_queue" label={t('Queue')}><Select className="w-48" value={queue} onChange={(e) => setQueue(e.target.value)} data-testid="sla-queue">
          <option value="">{t('All queues')}</option>{(queues.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></Field>
        <Button variant="outline" onClick={exportCsv} disabled={!q.data}><Download /> {t('Export CSV')}</Button>
        <p className="ml-auto max-w-xl text-xs text-muted-foreground">
          {t('Target: {{target}}% answered within {{sec}} s, at most {{abandon}}% abandoned.', { target: tot.target_pct ?? s.sla_target_pct, sec: tot.answer_sec ?? s.sla_answer_sec, abandon: tot.abandon_max_pct ?? s.sla_abandon_max_pct })}
          {' '}{t('Callers who hang up within {{sec}} s are not counted. Queues can have their own targets (Queues page); the defaults are in Settings.', { sec: s.sla_abandon_grace_sec ?? 5 })}
        </p>
      </div>
      <ErrorBox error={q.error} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label={t('Service level')} value={<span data-testid="sla-total-sl"><SlaCell kind="sl" value={tot.service_level_pct} bad={tot.breach?.service_level} /></span>}
          hint={t('{{a}} of {{b}} answered in time', { a: tot.answered_in_sla ?? 0, b: tot.counted ?? 0 })} />
        <Stat label={t('Abandoned')} value={<span data-testid="sla-total-ab"><SlaCell kind="ab" value={tot.abandon_pct} bad={tot.breach?.abandon} /></span>}
          hint={t('{{n}} calls · {{short}} within {{sec}} s not counted', { n: tot.abandoned ?? 0, short: tot.short_abandoned ?? 0, sec: s.sla_abandon_grace_sec ?? 5 })} />
        <Stat label={t('Queue calls')} value={tot.offered ?? 0} hint={t('{{answered}} answered · {{waiting}} waiting now', { answered: tot.answered ?? 0, waiting: tot.pending ?? 0 })} />
        <Stat label={t('Avg time to answer')} value={secs(tot.avg_answer_sec)} />
        <Stat label={t('Queues missing a target')} value={<span data-testid="sla-breached">{d.breached_queues ?? 0}</span>} hint={t('{{n}} alerts in the period', { n: (d.alerts ?? []).length })} />
      </div>
      <Card><CardHeader><CardTitle>{t('Per queue')}</CardTitle></CardHeader><CardContent>
        <div data-testid="sla-queues">
          <DataTable rowKey={(r) => String(r.key)} rows={d.queues ?? []} empty={t('No queue calls in this period.')} columns={[
            { key: 'name', label: t('Queue') }, { key: 'counted', label: t('Calls counted') },
            { key: 'service_level_pct', label: t('Service level'), render: (r) => <SlaCell kind="sl" value={r.service_level_pct} target={r.target_pct} bad={r.breach?.service_level} /> },
            { key: 'answer_sec', label: t('Answer within'), render: (r) => t('{{n}} s', { n: r.answer_sec }) },
            { key: 'abandon_pct', label: t('Abandoned'), render: (r) => <SlaCell kind="ab" value={r.abandon_pct} target={r.abandon_max_pct} bad={r.breach?.abandon} /> },
            { key: 'avg_answer_sec', label: t('Avg time to answer'), render: (r) => secs(r.avg_answer_sec) },
            { key: 'hours_on_target', label: t('Hours on target'), render: (r) => r.hours_counted ? t('{{a}} of {{b}}', { a: r.hours_on_target, b: r.hours_counted }) : '-' },
            { key: 'breach', label: '', render: (r) => (r.breach?.service_level || r.breach?.abandon) ? <Badge variant="destructive">{t('breach')}</Badge> : r.counted < (s.sla_min_calls ?? 0) ? <span className="text-xs text-muted-foreground">{t('too few calls to judge')}</span> : <Badge variant="success">{t('on target')}</Badge> }]} />
        </div>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>{d.by === 'day' ? t('Service level per day') : d.by === 'quarter' ? t('Service level per 15 minutes') : t('Service level per hour')}</CardTitle></CardHeader><CardContent>
        <div className="grid gap-1.5" data-testid="sla-periods">
          {periods.length === 0 && <p className="text-sm text-muted-foreground">{t('No queue calls in this period.')}</p>}
          {periods.map((p) => {
            const v = Number(p.service_level_pct ?? 0), target = Number(tot.target_pct ?? s.sla_target_pct ?? 80);
            return (
              <div key={p.key} className="flex items-center gap-2 text-sm">
                <div className="w-36 shrink-0 text-muted-foreground">{d.by === 'day' ? p.key : fmtTime(String(p.key) + ':00')}</div>
                <div className="relative h-4 flex-1 rounded bg-muted">
                  <div className={`h-4 rounded ${p.service_level_pct == null ? '' : v < target ? 'bg-destructive/70' : 'bg-success/70'}`} style={{ width: `${v}%` }} />
                  <div className="absolute inset-y-0 border-l-2 border-dashed border-foreground/50" style={{ left: `${target}%` }} title={t('target {{n}}%', { n: target })} />
                </div>
                <div className="w-28 text-right tabular-nums">{pc(p.service_level_pct)} · {p.counted}</div>
              </div>
            );
          })}
        </div>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>{t('Breaches')}</CardTitle></CardHeader><CardContent>
        <div data-testid="sla-alerts">
          <DataTable rowKey={(r) => r.id} rows={d.alerts ?? []} empty={t('No breaches in this period.')} columns={[
            { key: 'queue_name', label: t('Queue') }, { key: 'kind', label: t('Target missed'), render: (r) => { const k = ({ SERVICE_LEVEL: 'Service level', ABANDON: 'Abandon rate', LONG_WAIT: 'Long wait' } as Record<string, string>)[r.kind]; return k ? t(k) : r.kind; } },
            { key: 'opened_at', label: t('From'), render: (r) => fmtTime(r.opened_at) },
            { key: 'closed_at', label: t('Until'), render: (r) => r.closed_at ? fmtTime(r.closed_at) : <Badge variant="destructive">{t('still open')}</Badge> },
            { key: 'duration_sec', label: t('Lasted'), render: (r) => fmtDuration(r.duration_sec) },
            { key: 'worst_value', label: t('Worst'), render: (r) => r.kind === 'LONG_WAIT' ? t('{{value}} (max {{max}})', { value: fmtDuration(r.worst_value), max: fmtDuration(r.target_value) }) : t('{{value}} (target {{n}}%)', { value: pc(r.worst_value), n: Number(r.target_value) }) },
            { key: 'waiting_at_open', label: t('Waiting then') },
            { key: 'acked_by', label: t('Acknowledged'), render: (r) => r.acked_by ? `${r.acked_by} · ${fmtTime(r.acked_at)}` : '-' }]} />
        </div>
      </CardContent></Card>
    </div>
  );
}

function AgentReport({ from }: { from: string }) {
  const q = useQuery({ queryKey: ['rep-agents', from], queryFn: () => api('/reports/agents', { query: { from } }) });
  const rows: any[] = Array.isArray(q.data?.agents) ? q.data.agents : [];
  return (<>
    <ErrorBox error={q.error} />
    <DataTable rowKey={(r) => r.agent_id} rows={rows} columns={[
      { key: 'code', label: t('Agent') }, { key: 'calls_handled', label: t('Calls') }, { key: 'logged_in_sec', label: t('Logged in'), render: (r) => fmtDuration(r.logged_in_sec) },
      { key: 'ready_sec', label: t('Ready'), render: (r) => fmtDuration(r.ready_sec) }, { key: 'in_call_sec', label: t('In call'), render: (r) => fmtDuration(r.in_call_sec) },
      { key: 'talk_sec', label: t('Talk'), render: (r) => fmtDuration(r.talk_sec) }, { key: 'wrapup_sec', label: t('Wrap-up'), render: (r) => fmtDuration(r.wrapup_sec) },
      { key: 'paused_sec', label: t('Paused'), render: (r) => fmtDuration(r.paused_sec) }, { key: 'utilization', label: t('Utilization'), render: (r) => pct(r.utilization) }]} />
  </>);
}

function Minutes() {
  const [scope, setScope] = React.useState('TENANT');
  const [id, setId] = React.useState('');
  const q = useQuery({ queryKey: ['rep-min', scope, id], queryFn: () => api<{ rows: any[] }>('/reports/minutes', { query: { scope, id } }), refetchInterval: 60000 });
  const rows = q.data?.rows ?? [];
  const max = Math.max(1, ...rows.map((r) => Number(r.calls) || 0), ...rows.map((r) => Number(r.peak_concurrent) || 0));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between"><CardTitle>{t('Last hour, per minute')}</CardTitle>
        <div className="flex gap-2">
          <Select className="w-36" value={scope} onChange={(e) => setScope(e.target.value)}>{['TENANT', 'CAMPAIGN', 'TRUNK', 'QUEUE', 'SYSTEM'].map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}</Select>
          {['CAMPAIGN', 'TRUNK', 'QUEUE'].includes(scope) && <Input className="w-20" placeholder="id" value={id} onChange={(e) => setId(e.target.value)} />}
        </div>
      </CardHeader>
      <CardContent>
        <ErrorBox error={q.error} />
        {rows.length === 0 ? <div className="text-sm text-muted-foreground">{t('No data for this period.')}</div> : (
          <div className="flex h-40 items-end gap-0.5" role="img" aria-label={t('calls per minute')}>
            {rows.map((r) => (
              <div key={r.minute} className="group relative flex flex-1 flex-col justify-end" title={t('{{time}}: {{calls}} calls, {{answered}} answered, peak {{peak}} concurrent', { time: fmtTime(r.minute), calls: r.calls, answered: r.answered, peak: r.peak_concurrent })}>
                <div className="w-full rounded-t bg-primary/70" style={{ height: `${(Number(r.calls) / max) * 100}%` }} />
                <div className="absolute w-full border-t-2 border-warning" style={{ bottom: `${(Number(r.peak_concurrent) / max) * 100}%` }} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">{t('Bars: calls started. Line: peak concurrent calls.')}</div>
      </CardContent>
    </Card>
  );
}

